import { useEffect, useState } from "react";
import { apiFetch } from "./auth";

/**
 * Who, if anyone, is signed in — for surfaces that render for BOTH visitors and members.
 *
 * THE BUG THIS EXISTS FOR (2026-08-01, owner report): the marketing nav rendered "Request access",
 * "Open the app" and "Sign in" all at once, permanently, with no idea whether anyone was signed in.
 * A returning member landed on their own product's home page and was asked to request access to it.
 * The owner's words: "on the homepage login or request access shows even when logged in, its very
 * confusing."
 *
 * Deliberately its own tiny hook in `lib/` rather than an import from `cma/api.ts`: the marketing
 * page must not depend on the CMA flow's module graph, or every visitor downloads the whole app
 * shell to render a header.
 *
 * `status` is three-valued on purpose. "unknown" is the first paint, before the round trip has
 * answered, and it is NOT the same as "out" — treating it as "out" is what makes an auth-aware
 * header flicker "Sign in" at somebody who is already signed in. Callers should render the
 * neutral/shared affordances while unknown and only commit once it resolves.
 */
export type SessionStatus = "unknown" | "in" | "out";

export interface Session {
  status: SessionStatus;
  /** The signed-in user's id, from `/api/me` (S10, 2026-09-23). Lets a surface offer a link only on
   *  a record the server will actually open for THIS user — e.g. the appraisal view, which is
   *  owner-only while the shelf lists the whole organization's briefs. Null until known. */
  id: string | null;
  email: string | null;
  name: string | null;
  /**
   * EFFECTIVE admin privilege — designated AND this session cleared a second factor. It is the
   * server's `is_admin`, unchanged, and it is what an admin-only link may be shown for. A link
   * that is visible but 403s on click is worse than no link.
   */
  isAdmin: boolean;
  /**
   * True for somebody who IS an admin on a session that could not prove it (SEC-01: no second
   * factor could be delivered and the deployment has not opted out). Carried separately so a
   * surface can say "sign in again to finish" rather than implying the designation was lost.
   */
  adminPendingMfa: boolean;
}

interface MeShape {
  user?: {
    id?: string | null;
    email?: string | null;
    name?: string | null;
    is_admin?: boolean | null;
    admin_pending_mfa?: boolean | null;
  } | null;
}

export function useSession(): Session {
  const [session, setSession] = useState<Session>({
    status: "unknown", id: null, email: null, name: null, isAdmin: false, adminPendingMfa: false,
  });

  useEffect(() => {
    let cancelled = false;
    // No redirect on 401: "nobody is signed in" is a legitimate answer here, not a session expiry
    // to bounce on. Any failure at all resolves to "out" — a header must never hang on "unknown".
    apiFetch<MeShape>("/api/me", { onUnauthorized: () => {} })
      .then((r) => {
        if (cancelled) return;
        const u = r?.user;
        setSession(u?.email
          ? {
              status: "in",
              id: typeof u.id === "string" && u.id ? u.id : null,
              email: u.email ?? null,
              name: u.name ?? null,
              // `=== true`, not truthiness: an older server with no such key must leave the
              // admin link hidden rather than showing one that 403s.
              isAdmin: u.is_admin === true,
              adminPendingMfa: u.admin_pending_mfa === true,
            }
          : { status: "out", id: null, email: null, name: null, isAdmin: false, adminPendingMfa: false });
      })
      .catch(() => {
        if (!cancelled) setSession({ status: "out", id: null, email: null, name: null, isAdmin: false, adminPendingMfa: false });
      });
    return () => { cancelled = true; };
  }, []);

  return session;
}
